/**
 * Core-100 R4 W2 — CORE-S: author → submit → (a creator admin) publish or reject. The creator sees their drafts,
 * what they submitted and every decision with its reason; the admin sees a queue of submitted work only.
 * The server decides every act (useCreatorSubmissions); this component only renders and asks.
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  useMySubmissions, useSubmissionQueue, useCreateDraft, useSubmitSubmission, useDecideSubmission,
  type CreatorSubmission, type SubmissionStatus,
} from '@/hooks/useCreatorSubmissions';

const STATUS_LABEL: Record<SubmissionStatus, string> = {
  draft: 'Draft (only you can see it)',
  submitted: 'Submitted for review',
  published: 'Published',
  rejected: 'Not published',
};

const errorText = (e: unknown) => (e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e));

/** The creator's own authoring and submission view. */
export function CreatorSubmissionsPanel({ creatorId }: { creatorId: string | null | undefined }) {
  const { toast } = useToast();
  const { data: mine } = useMySubmissions();
  const createDraft = useCreateDraft();
  const submit = useSubmitSubmission();
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  if (!creatorId) return null;

  const onCreate = () => {
    createDraft.mutate({ creatorId, title, accessUrl: url, artifactType: 'short_video' }, {
      onSuccess: () => { setTitle(''); setUrl(''); toast({ title: 'Draft saved', description: 'Only you can see a draft until you submit it.' }); },
      onError: (e) => toast({ title: 'Draft not saved', description: errorText(e), variant: 'destructive' }),
    });
  };

  return (
    <Card data-testid="creator-submissions">
      <CardHeader>
        <CardTitle className="text-base">Your content</CardTitle>
        <CardDescription>
          You author and submit. Publication is a separate decision by OptiBay&apos;s content reviewers; your authorship
          stays with the content either way.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input aria-label="Title" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input aria-label="Link (https://)" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
          <Button size="sm" onClick={onCreate} disabled={!title.trim() || !url.trim() || createDraft.isPending}>Save draft</Button>
        </div>
        <ul className="space-y-2" data-testid="creator-submission-list">
          {(mine ?? []).map((s) => (
            <li key={s.id} className="rounded border p-2 text-sm" data-testid={`submission-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{s.title}</span>
                <Badge variant={s.status === 'rejected' ? 'destructive' : 'secondary'}>{STATUS_LABEL[s.status]}</Badge>
              </div>
              {s.status === 'rejected' && s.decision_reason && (
                <p className="text-xs text-muted-foreground">Reviewer&apos;s reason: {s.decision_reason}</p>
              )}
              {s.status === 'draft' && (
                <Button size="sm" variant="outline" className="mt-1" disabled={submit.isPending}
                  onClick={() => submit.mutate(s.id, {
                    onError: (e) => toast({ title: 'Not submitted', description: errorText(e), variant: 'destructive' }),
                  })}>
                  Submit for review
                </Button>
              )}
            </li>
          ))}
          {mine && mine.length === 0 && <li className="text-xs text-muted-foreground">Nothing authored yet.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}

/** The creator admin's review queue (submitted work only; an admin never decides their own submission). */
export function SubmissionReviewQueue({ enabled }: { enabled: boolean }) {
  const { toast } = useToast();
  const { data: queue } = useSubmissionQueue(enabled);
  const decide = useDecideSubmission();
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const act = (s: CreatorSubmission, decision: 'publish' | 'reject') =>
    decide.mutate({ id: s.id, decision, reason: reasons[s.id] }, {
      onSuccess: (r) => toast({ title: r.status === 'published' ? 'Published' : 'Not published' }),
      onError: (e) => toast({ title: 'Decision refused', description: errorText(e), variant: 'destructive' }),
    });

  if (!enabled) return null;
  return (
    <Card data-testid="submission-review-queue">
      <CardHeader>
        <CardTitle className="text-base">Submitted for review</CardTitle>
        <CardDescription>Publishing adds the item to the catalogue under its creator&apos;s authorship. A rejection needs a reason the creator will see.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {(queue ?? []).map((s) => (
            <li key={s.id} className="rounded border p-2 text-sm space-y-1">
              <div className="font-medium">{s.title}</div>
              <div className="text-xs text-muted-foreground break-all">{s.access_url}</div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input aria-label="Reason" placeholder="Reason (required to reject)" value={reasons[s.id] ?? ''}
                  onChange={(e) => setReasons({ ...reasons, [s.id]: e.target.value })} />
                <Button size="sm" disabled={decide.isPending} onClick={() => act(s, 'publish')}>Publish</Button>
                <Button size="sm" variant="outline" disabled={decide.isPending || !(reasons[s.id] ?? '').trim()} onClick={() => act(s, 'reject')}>Reject</Button>
              </div>
            </li>
          ))}
          {queue && queue.length === 0 && <li className="text-xs text-muted-foreground">Nothing waiting.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}
